/**
 * EK020 | Badge System Refactor: Badge analytics and events tracking
 *
 * Pushes GTM dataLayer events for the EK021 badge system:
 * - badge_impression: badge enters the viewport (once per unique badge per page load)
 *   params: badge_category, badge_label, badge_placement, product_id, page_type, badge_id
 * - badge_click: badge or the product card containing it is clicked
 *   params: badge_category, badge_label, badge_placement, product_id, page_type, badge_id
 * - badge_suppressed: an override hid a badge that would otherwise have shown
 *   (e.g. Out of stock suppresses Campaign), read from server-rendered
 *   [data-badge-suppressed] markers
 *   params: suppressed_category, suppressed_label, override_reason, product_id
 *
 * Badges are identified by the data-badge-* attributes rendered by
 * snippets/badge.liquid and the suppression markers rendered by
 * snippets/badge-resolver.liquid and snippets/price-badges.liquid.
 * Dynamically injected content (variant re-renders, cart drawer, upsell
 * sliders) is covered by a MutationObserver.
 *
 * GTM tag configuration is supplied by the Ekster Ecommerce team; this file
 * only populates the dataLayer.
 */
(function () {
  'use strict';

  var BADGE_SELECTOR = '[data-badge-category]';
  var SUPPRESSED_SELECTOR = '[data-badge-suppressed]';

  var seenImpressions = {};
  var seenSuppressions = {};

  var currentScript = document.currentScript;
  var pageType = (currentScript && currentScript.getAttribute('data-page-type')) || '';
  if (!pageType) {
    // Fallback: derive from the body template class rendered by html-body-class.liquid
    var match = (document.body.className || '').match(/(?:^|\s)template-([a-z0-9]+)(?:\s|$)/);
    pageType = match ? match[1] : '';
  }

  function pushEvent(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function badgeData(el) {
    return {
      badge_category: el.getAttribute('data-badge-category') || '',
      badge_label: (el.textContent || '').trim(),
      badge_placement: el.getAttribute('data-badge-placement') || '',
      badge_id: el.getAttribute('data-badge-id') || '',
      product_id: el.getAttribute('data-badge-product-id') || '',
      page_type: pageType
    };
  }

  function badgeKey(data) {
    return [
      data.badge_category,
      data.badge_id,
      data.badge_label,
      data.badge_placement,
      data.product_id
    ].join('|');
  }

  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);

          var data = badgeData(entry.target);
          var key = badgeKey(data);
          if (seenImpressions[key]) return;
          seenImpressions[key] = true;

          pushEvent({
            event: 'badge_impression',
            badge_category: data.badge_category,
            badge_label: data.badge_label,
            badge_placement: data.badge_placement,
            badge_id: data.badge_id,
            product_id: data.product_id,
            page_type: data.page_type
          });
        });
      },
      { threshold: 0.5 }
    );
  }

  function trackSuppression(el) {
    if (el.__ekbaSuppressionTracked) return;
    el.__ekbaSuppressionTracked = true;

    var payload = {
      event: 'badge_suppressed',
      suppressed_category: el.getAttribute('data-suppressed-category') || '',
      suppressed_label: el.getAttribute('data-suppressed-label') || '',
      override_reason: el.getAttribute('data-override-reason') || '',
      product_id: el.getAttribute('data-badge-product-id') || ''
    };
    var key = [
      payload.suppressed_category,
      payload.suppressed_label,
      payload.override_reason,
      payload.product_id
    ].join('|');
    if (seenSuppressions[key]) return;
    seenSuppressions[key] = true;

    pushEvent(payload);
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;

    var badges = [];
    if (root.matches && root.matches(BADGE_SELECTOR)) badges.push(root);
    if (root.querySelectorAll) {
      badges = badges.concat(Array.prototype.slice.call(root.querySelectorAll(BADGE_SELECTOR)));
    }
    badges.forEach(function (el) {
      if (el.__ekbaObserved) return;
      el.__ekbaObserved = true;
      if (observer) observer.observe(el);
    });

    var markers = [];
    if (root.matches && root.matches(SUPPRESSED_SELECTOR)) markers.push(root);
    if (root.querySelectorAll) {
      markers = markers.concat(
        Array.prototype.slice.call(root.querySelectorAll(SUPPRESSED_SELECTOR))
      );
    }
    markers.forEach(trackSuppression);
  }

  function onClick(event) {
    var target = event.target;
    if (!target || target.nodeType !== 1) {
      target = target && target.parentElement;
      if (!target) return;
    }

    // Direct badge click, or a click anywhere on a product card that carries a badge
    var badge = target.closest ? target.closest(BADGE_SELECTOR) : null;
    if (!badge && target.closest) {
      var card = target.closest('.product-card');
      if (card) badge = card.querySelector(BADGE_SELECTOR);
    }
    if (!badge) return;

    var data = badgeData(badge);
    pushEvent({
      event: 'badge_click',
      badge_category: data.badge_category,
      badge_label: data.badge_label,
      badge_placement: data.badge_placement,
      badge_id: data.badge_id,
      product_id: data.product_id,
      page_type: data.page_type
    });
  }

  function init() {
    scan(document.body);

    document.addEventListener('click', onClick, true);

    if ('MutationObserver' in window) {
      var pending = [];
      var scheduled = false;

      var mutationObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) pending.push(added[j]);
          }
        }
        if (pending.length && !scheduled) {
          scheduled = true;
          window.requestAnimationFrame(function () {
            scheduled = false;
            var batch = pending.splice(0, pending.length);
            batch.forEach(scan);
          });
        }
      });

      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
